import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExamSelfCheckDto, SelfMark } from '../../types/course';
import { ReportMarkdown } from '../ReportMarkdown';
import { InlineText } from './InlineText';
import styles from './Course.module.css';

interface Props {
  snapshot: ExamSelfCheckDto;
  onMark: (mark: SelfMark) => void;
  onRetry: () => void;
  onDiscuss?: (number: number, attemptId: string) => void;
  busy?: boolean;
  pendingMark?: SelfMark;
  error?: string | null;
}
/** One decision for the whole saved comparison; self results have no numerical score. */
export function ExamSelfCheck({ snapshot, onMark, onRetry, onDiscuss, busy = false, error, pendingMark }: Props): JSX.Element {
  const { t } = useTranslation('course');
  const summary = useRef<HTMLParagraphElement>(null);
  useEffect(() => { if (error) summary.current?.focus(); }, [error]);
  const attempt = snapshot.completedAttempt;
  return (
    <section className={styles.solution} data-testid="exam-self-check" aria-label={t('exam.methodSelf')}>
      <h3 className={styles.h4}>{t('exam.methodSelf')}</h3>
      <p className={styles.text}>{t('exam.compare')}</p>
      {snapshot.items.map((item) => (
        <article key={item.number} className={styles.card} data-testid={`self-item-${item.number}`}>
          <h4 className={styles.h4}>{item.number}. <InlineText text={item.title} /></h4>
          <p className={styles.text}><InlineText text={item.prompt} /></p>
          <div className={styles.comparison}>
            <div><h5>{t('exam.yourAnswer')}</h5><p className={styles.text}>{item.answer}</p></div>
            <div><h5>{t('exam.solution')}</h5><ReportMarkdown markdown={item.solution} /></div>
          </div>
          {attempt?.status === 'graded' && typeof attempt.passed === 'boolean' && attempt.versionNumber === snapshot.versionNumber
            && (attempt.gradingMethod === 'ai' || attempt.gradingMethod === 'self') && onDiscuss
            && <button type="button" className={styles.secondary} onClick={() => onDiscuss(item.number, attempt.id)}>{t('chat.discuss')}</button>}
        </article>
      ))}
      <h4 className={styles.h4}>{t('exam.criteria')}</h4>
      <ReportMarkdown markdown={snapshot.scoring} />
      {error && <><p ref={summary} tabIndex={-1} role="alert" className={styles.error}>{error}</p><button type="button" className={styles.secondary} onClick={onRetry} data-testid="self-retry">{t('exam.retrySelf')}</button></>}
      {!attempt ? (
        <div className={styles.row}>
          <button type="button" className={styles.solid} disabled={busy || pendingMark === 'wrong'} onClick={() => onMark('ok')} data-testid="self-mark-ok">{t('exam.markOk')}</button>
          <button type="button" className={styles.secondary} disabled={busy || pendingMark === 'ok'} onClick={() => onMark('wrong')} data-testid="self-mark-wrong">{t('exam.markWrong')}</button>
        </div>
      ) : (
        <>
          <p role="status">{attempt.passed ? t('exam.markOk') : t('exam.markWrong')}</p>
          {!attempt.passed && <><p>{t('exam.returnLessons')}</p><ul>{snapshot.recommendations.lessonCodes.map((code) => <li key={code}><a href={`/course/lesson/${encodeURIComponent(code)}`}>{code}</a></li>)}</ul></>}
          <button type="button" className={styles.secondary} onClick={onRetry} data-testid={error ? "self-retry-completed" : "self-retry"}>{t('exam.retrySelf')}</button>
        </>
      )}
    </section>
  );
}
